import React from 'react';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { Status } from 'components/Status';
import { Label, Text } from 'components/Typography';
import { lineHeight, theme, toRem } from 'design';
import { Appearance } from 'design/types';
import { DisbursementMethod, IPayoutStats, PayoutStatus } from 'types';
import { sumBy } from 'utils/arrays';
import { centsToDollarsDisplay, centsToNegativeDollarsDisplay } from 'utils/currency';
import { getPayoutNetTotalByStatus, getPayoutStatusCount } from 'utils/payout';

const Layout = styled.section`
  width: 100%;
`;

const PaymentTotals = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  gap: ${toRem(theme.space.xl)};

  @media (min-width: ${theme.breakpoint.large}px) {
    flex-flow: row nowrap;
    gap: 0;
  }
`;

const TotalSection = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  align-items: center;

  label {
    text-transform: uppercase;
    font-size: ${toRem(theme.font.size.xxs)};
    line-height: ${lineHeight(theme.font.size.xxs)};
  }

  @media (min-width: ${theme.breakpoint.large}px) {
    padding-right: ${toRem(theme.space.sm)};

    &:first-child {
      border-right: ${toRem(1)} solid ${theme.color.neutral[200]};
    }
  }
`;

const TotalRow = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: center;
  width: 100%;
  margin-top: ${toRem(theme.space.lg)};
  gap: ${toRem(theme.space.md)};

  label,
  .status-label {
    text-transform: capitalize;
    font-size: ${toRem(theme.font.size.xxs)};
    line-height: ${lineHeight(theme.font.size.xxs)};
    font-weight: ${theme.font.weight.regular};
    color: ${theme.color.neutral[500]};
  }
`;

const TotalValue = styled.div`
  display: flex;
  flex-flow: column nowrap;
  flex: 1 1 auto;
  align-items: center;

  span.value {
    margin-top: ${toRem(theme.space.xs)};
  }

  p {
    font-size: ${toRem(theme.font.size.lg)};
    line-height: ${lineHeight(theme.font.size.lg)};
    margin-top: ${toRem(theme.space.xs)};
    font-weight: ${theme.font.weight.semiBold};
  }
`;

const LoadingSection = ({ count }: { count: number }) => (
  <>
    <Skeleton width={100} />
    <TotalRow>
      {Array.from({ length: count }).map((_, index) => (
        <TotalValue key={index}>
          <Skeleton width={100} />
          <Skeleton width={100} className="value" />
        </TotalValue>
      ))}
    </TotalRow>
  </>
);

interface IPayoutStatsProps {
  dataCy?: string;
  isLoading: boolean;
  stats?: IPayoutStats;
}

export const PayoutStats = ({ stats, isLoading, dataCy = 'payout-stats' }: IPayoutStatsProps) => {
  const statsByDate = stats?.byDate;
  const grossTotal = sumBy(statsByDate, 'gross');
  const netTotal = sumBy(statsByDate, 'net');
  const holdbackTotal = sumBy(statsByDate, 'holdbacks');
  const payrollTotal = sumBy(statsByDate, DisbursementMethod.PAYROLL);
  const cashTotal = sumBy(statsByDate, DisbursementMethod.CASH);
  const instantTotal = sumBy(statsByDate, DisbursementMethod.INSTANT);

  const statsByStatus = stats?.byStatus;
  const paidCount = getPayoutStatusCount(statsByStatus, PayoutStatus.PAID);
  const pendingCount = getPayoutStatusCount(statsByStatus, PayoutStatus.PENDING);
  const expiredCount = getPayoutStatusCount(statsByStatus, PayoutStatus.EXPIRED);
  const canceledCount = getPayoutStatusCount(statsByStatus, PayoutStatus.CANCELED);

  const paidTotal = getPayoutNetTotalByStatus(statsByStatus, PayoutStatus.PAID);
  const pendingTotal = getPayoutNetTotalByStatus(statsByStatus, PayoutStatus.PENDING);
  const expiredTotal = getPayoutNetTotalByStatus(statsByStatus, PayoutStatus.EXPIRED);
  const canceledTotal = getPayoutNetTotalByStatus(statsByStatus, PayoutStatus.CANCELED);

  return (
    <Layout data-cy={dataCy}>
      <PaymentTotals data-cy={`${dataCy}-totals`}>
        <TotalSection>
          {isLoading ? (
            <LoadingSection count={6} />
          ) : (
            <>
              <Label>Payment Overview</Label>
              <TotalRow data-cy={`${dataCy}-overview-row`}>
                <TotalValue data-cy={`${dataCy}-gross`}>
                  <Label>Gross</Label>
                  <Text size="small">{centsToDollarsDisplay(grossTotal)}</Text>
                </TotalValue>
                <TotalValue data-cy={`${dataCy}-holdback`}>
                  <Label>Holdback</Label>
                  <Text size="small">{centsToNegativeDollarsDisplay(holdbackTotal)}</Text>
                </TotalValue>
                <TotalValue data-cy={`${dataCy}-net`}>
                  <Label>Net</Label>
                  <Text size="small">{centsToDollarsDisplay(netTotal)}</Text>
                </TotalValue>
                <TotalValue data-cy={`${dataCy}-payroll`}>
                  <Label>Payroll</Label>
                  <Text size="small">{centsToDollarsDisplay(payrollTotal)}</Text>
                </TotalValue>
                <TotalValue data-cy={`${dataCy}-cash`}>
                  <Label>Cash</Label>
                  <Text size="small">{centsToDollarsDisplay(cashTotal)}</Text>
                </TotalValue>
                <TotalValue data-cy={`${dataCy}-instant`}>
                  <Label>Instant</Label>
                  <Text size="small">{centsToDollarsDisplay(instantTotal)}</Text>
                </TotalValue>
              </TotalRow>
            </>
          )}
        </TotalSection>
        <TotalSection>
          {isLoading ? (
            <LoadingSection count={4} />
          ) : (
            <>
              <Label>Payment Status</Label>
              <TotalRow data-cy={`${dataCy}-status-row`}>
                <TotalValue data-cy={`${dataCy}-paid`}>
                  <Status appearance={Appearance.SUCCESS} label={`Paid (${paidCount || 0})`} className="status-label" />
                  <Text size="small">{centsToDollarsDisplay(paidTotal || 0)}</Text>
                </TotalValue>
                <TotalValue data-cy={`${dataCy}-pending`}>
                  <Status appearance={Appearance.WARNING} label={`Pending (${pendingCount || 0})`} className="status-label" />
                  <Text size="small">{centsToDollarsDisplay(pendingTotal || 0)}</Text>
                </TotalValue>
                <TotalValue data-cy={`${dataCy}-canceled`}>
                  <Status appearance={Appearance.NEUTRAL} label={`Canceled (${canceledCount || 0})`} className="status-label" />
                  <Text size="small">{centsToDollarsDisplay(canceledTotal || 0)}</Text>
                </TotalValue>
                <TotalValue data-cy={`${dataCy}-expired`}>
                  <Status appearance={Appearance.DANGER} label={`Expired (${expiredCount || 0})`} className="status-label" />
                  <Text size="small">{centsToDollarsDisplay(expiredTotal || 0)}</Text>
                </TotalValue>
              </TotalRow>
            </>
          )}
        </TotalSection>
      </PaymentTotals>
    </Layout>
  );
};
